import { ArrowBack } from '@mui/icons-material';
import {
  Box,
  Button,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from '../../../utils/axiosInstance';

const StatisticUserDetail = () => {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isNonMobile = useMediaQuery('(min-width:600px)');

  // 날짜 범위 (URL 상태에서 가져오기)
  const { startDate, endDate } = location.state || { 
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  };

  // 데이터 상태
  const [loading, setLoading] = useState(true);
  const [memberDetail, setMemberDetail] = useState({
    game_company_stats: [],
    game_vendor_stats: [],
    game_type_stats: [],
    daily_stats: []
  });
  const [tabValue, setTabValue] = useState(0);

  // 페이지네이션 상태
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  // 탭 변경 핸들러
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0); // 탭 변경 시 페이지 초기화
  };

  // 페이지 변경 핸들러
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // 보여지는 행 개수 변경 핸들러
  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 뒤로가기 핸들러
  const handleBack = () => {
    navigate(-1);
  };

  // 해당 유저의 상세 통계 데이터 로드
  useEffect(() => {
    const fetchUserDetailStats = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/member/betting/statistics', {
          params: {
            mem_id: userId,
            start: startDate,
            end: endDate,
          }
        });

        if (response.data.success && response.data.member_detail) {
          setMemberDetail(response.data.member_detail);
        } else {
          console.error('사용자 상세 통계 데이터 가져오기 실패:', response.data.message || '데이터가 없습니다.');
        }
      } catch (error) {
        console.error('사용자 상세 통계 가져오기 중 오류 발생:', error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserDetailStats();
    }
  }, [userId, startDate, endDate]);

  // 현재 선택된 탭의 데이터 및 컬럼 가져오기
  const getCurrentTabData = () => {
    switch (tabValue) {
      case 0: // 일별 통계
        return memberDetail.daily_stats;
      case 1: // 게임회사별 통계
        return memberDetail.game_company_stats;
      case 2: // 벤더별 통계
        return memberDetail.game_vendor_stats;
      case 3: // 게임 유형별 통계
        return memberDetail.game_type_stats;
      default:
        return [];
    }
  };

  // 금액 포맷팅 함수
  const formatNumber = (number) => {
    return number ? Number(number).toLocaleString('ko-KR') : '0';
  };

  return (
    <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
      <Paper sx={{ p: isNonMobile ? 3 : 2, borderRadius: 2 }}>
        {/* 헤더 영역 */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button 
            startIcon={<ArrowBack />} 
            onClick={handleBack}
            sx={{ mr: 2, color: '#673ab7' }}
          >
            뒤로가기
          </Button>
          <Typography variant="h4" fontWeight="bold">
            '{userId}' 유저 상세 통계
          </Typography>
        </Box>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          조회 기간: {startDate} ~ {endDate}
        </Typography>

        {/* 탭 영역 */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="일별 통계" />
            <Tab label="게임회사별 통계" />
            <Tab label="벤더별 통계" />
            <Tab label="게임유형별 통계" />
          </Tabs>
        </Box>

        {/* 데이터 테이블 */}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress size={40} sx={{ color: '#673ab7' }} />
            <Typography sx={{ mt: 2 }}>통계 데이터 로딩 중...</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 600, overflowX: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {/* 일별 통계 */}
                  {tabValue === 0 && (
                    <>
                      <TableCell>날짜</TableCell>
                      <TableCell>배팅 금액</TableCell>
                      <TableCell>당첨 금액</TableCell>
                      <TableCell>수익</TableCell>
                      <TableCell>배팅 횟수</TableCell>
                      <TableCell>당첨 횟수</TableCell>
                    </>
                  )}
                  {/* 게임회사별 통계 */}
                  {tabValue === 1 && (
                    <>
                      <TableCell>게임사</TableCell>
                      <TableCell>배팅 금액</TableCell>
                      <TableCell>당첨 금액</TableCell>
                      <TableCell>수익</TableCell>
                      <TableCell>배팅 횟수</TableCell>
                      <TableCell>당첨 횟수</TableCell>
                    </>
                  )}
                  {/* 벤더별 통계 */}
                  {tabValue === 2 && (
                    <>
                      <TableCell>벤더사</TableCell>
                      <TableCell>배팅 금액</TableCell>
                      <TableCell>당첨 금액</TableCell>
                      <TableCell>수익</TableCell>
                      <TableCell>배팅 횟수</TableCell>
                      <TableCell>당첨 횟수</TableCell>
                    </>
                  )}
                  {/* 게임 유형별 통계 */}
                  {tabValue === 3 && (
                    <>
                      <TableCell>게임 유형</TableCell>
                      <TableCell>배팅 금액</TableCell>
                      <TableCell>당첨 금액</TableCell>
                      <TableCell>수익</TableCell>
                      <TableCell>배팅 횟수</TableCell>
                      <TableCell>당첨 횟수</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {getCurrentTabData().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1">데이터가 없습니다.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  getCurrentTabData()
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <TableRow key={index}>
                        {tabValue === 0 && (
                          <>
                            <TableCell>{row.date}</TableCell>
                            <TableCell>{formatNumber(row.betting_amount)} 원</TableCell>
                            <TableCell>{formatNumber(row.prize_amount)} 원</TableCell>
                            <TableCell 
                              sx={{ color: row.profit >= 0 ? 'success.main' : 'error.main' }}
                            >
                              {formatNumber(row.profit)} 원
                            </TableCell>
                            <TableCell>{formatNumber(row.bet_count)} 건</TableCell>
                            <TableCell>{formatNumber(row.win_count)} 건</TableCell>
                          </>
                        )}
                        {tabValue === 1 && (
                          <>
                            <TableCell>{row.game_company}</TableCell>
                            <TableCell>{formatNumber(row.betting_amount)} 원</TableCell>
                            <TableCell>{formatNumber(row.prize_amount)} 원</TableCell>
                            <TableCell 
                              sx={{ color: row.profit >= 0 ? 'success.main' : 'error.main' }}
                            >
                              {formatNumber(row.profit)} 원
                            </TableCell>
                            <TableCell>{formatNumber(row.bet_count)} 건</TableCell>
                            <TableCell>{formatNumber(row.win_count)} 건</TableCell>
                          </>
                        )}
                        {tabValue === 2 && (
                          <>
                            <TableCell>{row.game_vendor}</TableCell>
                            <TableCell>{formatNumber(row.betting_amount)} 원</TableCell>
                            <TableCell>{formatNumber(row.prize_amount)} 원</TableCell>
                            <TableCell 
                              sx={{ color: row.profit >= 0 ? 'success.main' : 'error.main' }}
                            >
                              {formatNumber(row.profit)} 원
                            </TableCell>
                            <TableCell>{formatNumber(row.bet_count)} 건</TableCell>
                            <TableCell>{formatNumber(row.win_count)} 건</TableCell>
                          </>
                        )}
                        {tabValue === 3 && (
                          <>
                            <TableCell>{row.game_type}</TableCell>
                            <TableCell>{formatNumber(row.betting_amount)} 원</TableCell>
                            <TableCell>{formatNumber(row.prize_amount)} 원</TableCell>
                            <TableCell 
                              sx={{ color: row.profit >= 0 ? 'success.main' : 'error.main' }}
                            >
                              {formatNumber(row.profit)} 원
                            </TableCell>
                            <TableCell>{formatNumber(row.bet_count)} 건</TableCell>
                            <TableCell>{formatNumber(row.win_count)} 건</TableCell>
                          </>
                        )}
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
            {getCurrentTabData().length > 0 && (
              <TablePagination
                rowsPerPageOptions={[10, 20, 30, 50, 100]}
                component="div"
                count={getCurrentTabData().length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage=""
                sx={{
                  marginTop: '12px',
                  borderRadius: 2,
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                  '& .MuiTablePagination-toolbar': {
                    justifyContent: 'center',
                  },
                  '& .MuiTablePagination-select': {
                    padding: '6px 10px',
                    border: '1px solid #eee',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    color: '#673ab7',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: '#f3e5f5',
                    },
                    '& .MuiSelect-icon': {
                      color: '#673ab7',
                    },
                  },
                  '& .MuiTablePagination-actions': {
                    color: '#673ab7',
                  },
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        maxHeight: 200,
                        borderRadius: 2,
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                        '& .MuiMenuItem-root': {
                          padding: '8px 16px',
                          '&:hover': {
                            backgroundColor: '#f3e5f5',
                          },
                        },
                      },
                    },
                  },
                }}
              />
            )}
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default StatisticUserDetail;